import { useState, useEffect, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import { useMutation, useQuery } from '@tanstack/react-query';
import {
  PageContainer,
  Button,
  Input,
  FormField,
  Alert,
  ErrorBoundary,
  useToast,
  phoneLengthError,
} from '@bbpos/ui';
import { userService } from '@/services/user.service';
import { locationService } from '@/services/location.service';
import { useAuthStore } from '@/stores/use-auth-store';
import { RoleSelector } from '@/components/features/users/RoleSelector';
import { LocationAssignment } from '@/components/features/users/LocationAssignment';
import type { TenantRole } from '@/types/user';
import { ROLE_LEVELS } from '@/types/user';
import { useTenantDial } from '@/hooks/use-tenant-dial';

const PANEL_CLASS =
  'rounded-2xl border border-slate-200/80 bg-white shadow-[0_12px_30px_-18px_rgba(15,23,42,0.22)]';

const E164_REGEX = /^\+[1-9]\d{6,14}$/;

function UserCreateContent() {
  const dial = useTenantDial();
  const navigate = useNavigate();
  const { toast } = useToast();
  const profile = useAuthStore((s) => s.profile);
  const businessType = profile?.tenant?.business_type ?? 'RETAIL';
  const currentRoleLevel = ROLE_LEVELS[profile?.role ?? ''] ?? 0;

  const [name, setName] = useState('');
  const [email, setEmail] = useState('');
  const [phone, setPhone] = useState('');
  const [role, setRole] = useState<TenantRole | undefined>();
  const [locationIds, setLocationIds] = useState<string[]>([]);
  const [errors, setErrors] = useState<Record<string, string>>({});
  const [step, setStep] = useState<'idle' | 'creating' | 'assigning' | 'inviting' | 'done'>('idle');

  const { data: locData } = useQuery({
    queryKey: ['locations', 'all'],
    queryFn: () => locationService.list({ limit: 100 }),
    staleTime: 60_000,
  });

  const locations = locData?.data ?? [];

  const createMutation = useMutation({
    mutationFn: async () => {
      setStep('creating');
      const user = await userService.create({
        name: name.trim(),
        email: email.trim(),
        role: role!,
        phone: phone || undefined,
        location_ids: locationIds,
      });

      setStep('inviting');
      await userService.sendInvite(user.id);

      setStep('done');
      return user;
    },
    onSuccess: (user) => {
      toast({
        title: `User '${user.name}' created and invite sent to ${user.email}`,
        variant: 'success',
      });
      navigate('/users');
    },
    onError: (err: Error) => {
      setStep('idle');
      const msg = err.message ?? '';

      if (msg.includes('EMAIL_DUPLICATE') || msg.includes('already registered')) {
        setErrors((prev) => ({ ...prev, email: 'A user with this email already exists' }));
      } else if (msg.includes('ROLE_ESCALATION')) {
        setErrors((prev) => ({ ...prev, role: 'You cannot assign a role equal to or higher than your own' }));
      }
      // Other errors (including read-only / 403) surface via the inline Alert below.
    },
  });

  function validate(): boolean {
    const errs: Record<string, string> = {};
    if (!name || name.length < 2) errs.name = 'Name must be at least 2 characters';
    if (name.length > 200) errs.name = 'Name must be at most 200 characters';
    if (!email || !/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(email)) errs.email = 'Enter a valid email address';
    if (phone && !E164_REGEX.test(phone)) errs.phone = 'Enter a valid phone number';
    else if (phoneLengthError(phone)) errs.phone = phoneLengthError(phone)!;
    if (!role) errs.role = 'Select a role';
    if (locationIds.length === 0) errs.locations = 'Assign at least one location';
    setErrors(errs);
    return Object.keys(errs).length === 0;
  }

  function handleSubmit(e: React.FormEvent) {
    e.preventDefault();
    if (!validate()) return;
    createMutation.mutate();
  }

  // Ctrl+Enter to submit
  const handleKeyDown = useCallback(
    (e: KeyboardEvent) => {
      if ((e.ctrlKey || e.metaKey) && e.key === 'Enter') {
        e.preventDefault();
        if (step === 'idle') {
          const form = document.querySelector<HTMLFormElement>('form');
          form?.requestSubmit();
        }
      }
    },
    [step],
  );

  useEffect(() => {
    window.addEventListener('keydown', handleKeyDown);
    return () => window.removeEventListener('keydown', handleKeyDown);
  }, [handleKeyDown]);

  const isSubmitting = step !== 'idle' && step !== 'done';

  return (
    <form onSubmit={handleSubmit} className="max-w-2xl mx-auto animate-fade-in">
      <div className={PANEL_CLASS}>
        <div className="p-6">
          {/* Personal Details */}
          <div className="flex items-center gap-3 mb-5">
            <div className="flex h-9 w-9 items-center justify-center rounded-xl bg-indigo-50 text-indigo-600">
              <svg className="h-5 w-5" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={1.5}>
                <path strokeLinecap="round" strokeLinejoin="round" d="M15.75 6a3.75 3.75 0 11-7.5 0 3.75 3.75 0 017.5 0zM4.501 20.118a7.5 7.5 0 0114.998 0A17.933 17.933 0 0112 21.75c-2.676 0-5.216-.584-7.499-1.632z" />
              </svg>
            </div>
            <div>
              <h3 className="text-sm font-semibold text-slate-900">Personal Details</h3>
              <p className="text-xs text-slate-500">Basic information about the team member</p>
            </div>
          </div>

          <div className="space-y-4">
            <FormField label="Full Name" htmlFor="user-name" required error={errors.name}>
              <Input
                id="user-name"
                placeholder="e.g., Priya Mehta"
                value={name}
                onChange={setName}
              />
            </FormField>

            <FormField label="Email Address" htmlFor="user-email" required error={errors.email} helperText="Invitation email will be sent here">
              <Input
                id="user-email"
                type="email"
                placeholder="user@business.com"
                value={email}
                onChange={setEmail}
              />
            </FormField>

            <FormField label="Phone Number" htmlFor="user-phone" error={errors.phone} helperText="Pick the country code, then enter the mobile number">
              <Input
                id="user-phone"
                variant="phone"
                defaultDial={dial}
                value={phone}
                onChange={setPhone}
              />
            </FormField>
          </div>

          {/* Role & Access */}
          <div className="mt-8 pt-6 border-t border-slate-100">
            <div className="flex items-center gap-3 mb-5">
              <div className="flex h-9 w-9 items-center justify-center rounded-xl bg-amber-50 text-amber-600">
                <svg className="h-5 w-5" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={1.5}>
                  <path strokeLinecap="round" strokeLinejoin="round" d="M9 12.75L11.25 15 15 9.75m-3-7.036A11.959 11.959 0 013.598 6 11.99 11.99 0 003 9.749c0 5.592 3.824 10.29 9 11.623 5.176-1.332 9-6.03 9-11.622 0-1.31-.21-2.571-.598-3.751h-.152c-3.196 0-6.1-1.248-8.25-3.285z" />
                </svg>
              </div>
              <div>
                <h3 className="text-sm font-semibold text-slate-900">Role & Access</h3>
                <p className="text-xs text-slate-500">Define permissions and location access</p>
              </div>
            </div>

            <div className="space-y-4">
              <div>
                <label className="block text-sm font-medium text-slate-700 mb-2">
                  Role <span className="text-rose-500">*</span>
                </label>
                <RoleSelector
                  value={role}
                  onChange={setRole}
                  businessType={businessType}
                  currentRoleLevel={currentRoleLevel}
                  disabled={isSubmitting}
                  mode="create"
                />
                {errors.role && <p className="mt-1.5 text-xs text-rose-600">{errors.role}</p>}
              </div>

              <div>
                <label className="block text-sm font-medium text-slate-700 mb-2">
                  Assigned Locations <span className="text-rose-500">*</span>
                </label>
                <LocationAssignment
                  locations={locations}
                  selected={locationIds}
                  onChange={setLocationIds}
                  disabled={isSubmitting}
                  error={errors.locations}
                />
              </div>
            </div>
          </div>

          {/* Actions */}
          <div className="flex items-center justify-between mt-8 pt-6 border-t border-slate-100">
            <Button variant="ghost" onClick={() => navigate('/users')} disabled={isSubmitting}>
              Cancel
            </Button>
            <div className="flex items-center gap-2">
              <span className="text-xs text-slate-400 hidden sm:inline">Ctrl+Enter to submit</span>
              <Button variant="primary" type="submit" disabled={isSubmitting}>
                {step === 'creating'
                  ? 'Creating user...'
                  : step === 'assigning'
                    ? 'Assigning locations...'
                    : step === 'inviting'
                      ? 'Sending invite...'
                      : 'Send Invite'}
              </Button>
            </div>
          </div>
        </div>
      </div>

      {createMutation.isError && !errors.email && !errors.role && (
        <div className="mt-4">
          <Alert
            variant="danger"
            title="Failed to create user"
            description={(createMutation.error as Error)?.message ?? 'An unexpected error occurred'}
          />
        </div>
      )}

      <div className="mt-4 rounded-2xl border border-sky-100 bg-sky-50/50 p-4">
        <div className="flex gap-3">
          <div className="flex h-8 w-8 shrink-0 items-center justify-center rounded-lg bg-sky-100 text-sky-600">
            <svg className="h-4 w-4" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}>
              <path strokeLinecap="round" strokeLinejoin="round" d="M11.25 11.25l.041-.02a.75.75 0 011.063.852l-.708 2.836a.75.75 0 001.063.853l.041-.021M21 12a9 9 0 11-18 0 9 9 0 0118 0zm-9-3.75h.008v.008H12V8.25z" />
            </svg>
          </div>
          <div>
            <p className="text-sm font-semibold text-sky-900">Role assignment rules</p>
            <p className="mt-1 text-sm text-sky-700">
              You can only assign roles below your own level. OWNER role is assigned during tenant provisioning only.
            </p>
          </div>
        </div>
      </div>
    </form>
  );
}

export function UserCreatePage() {
  return (
    <PageContainer
      title="Invite User"
      subtitle="Add a new team member and assign their role and locations"
      breadcrumbs={[{ label: 'Users', href: '/users' }, { label: 'Invite User' }]}
    >
      <ErrorBoundary>
        <UserCreateContent />
      </ErrorBoundary>
    </PageContainer>
  );
}
